import React, { useCallback, useEffect, useMemo, useRef, useState, type AnimationEvent, type MouseEvent } from 'react';
import { Button } from '@fiverr-private/button';
import { BarsIcon } from '@fiverr-private/visuals';
import { Text } from '@fiverr-private/typography';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Overlay } from '@fiverr-private/overlay';
import { I18n } from '@fiverr-private/i18n-react';
import { PACKAGE_CLASSNAME } from '../../../constants';
import { useHeaderContext } from '../../context/HeaderContext';
import { useNavigationBarReporter } from '../../../events/bianka/navigationBar';
import { CategoriesNavigationMetric, countCategoriesNavigation } from '../../../events/grafana/categoriesNavigation';
import { cmsConfigurationTransformer } from './transformer';
import { MOTION, MAX_PANEL_WIDTH, VIEWPORT_MARGIN } from './constants';
import { CategoriesMenuPanel } from './CategoriesMenuPanel';
import { panelEnter, panelExit } from './CategoriesMenuPanel.ve.css';

export const CATEGORIES_NAVIGATION_OVERLAY_ID = 'header-categories-navigation-overlay';
export const CATEGORIES_NAVIGATION_BOX_TEST_ID = 'header-categories-navigation-box';

const getHeaderBottom = () => document.querySelector(`.${PACKAGE_CLASSNAME}`)?.getBoundingClientRect().bottom ?? 0;

const getCardLeft = (button: HTMLElement) =>
    Math.max(
        VIEWPORT_MARGIN,
        Math.min(button.getBoundingClientRect().left, window.innerWidth - MAX_PANEL_WIDTH - VIEWPORT_MARGIN)
    );

export const CategoriesNavigation = () => {
    const { isNewCategoriesNavigation, categoriesCmsConfig } = useHeaderContext();
    const [isMounted, setIsMounted] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [top, setTop] = useState(0);
    const [left, setLeft] = useState(0);
    const toggleButton = useRef<HTMLElement | null>(null);
    const { reportOpen } = useNavigationBarReporter();
    const isOpen = isMounted && !isClosing;

    const categories = useMemo(() => cmsConfigurationTransformer(categoriesCmsConfig), [categoriesCmsConfig]);

    const close = useCallback(() => {
        countCategoriesNavigation(CategoriesNavigationMetric.CLOSE);
        setIsClosing(true);
    }, []);

    const unmount = useCallback(() => {
        setIsMounted(false);
        setIsClosing(false);
    }, []);

    const onExitAnimationEnd = (event: AnimationEvent<HTMLElement>) => {
        if (event.target === event.currentTarget) {
            unmount();
        }
    };

    const toggle = (event: MouseEvent<HTMLElement>) => {
        if (isOpen) {
            close();
            return;
        }

        countCategoriesNavigation(CategoriesNavigationMetric.OPEN);
        reportOpen();
        toggleButton.current = event.currentTarget;
        setTop(getHeaderBottom());
        setLeft(getCardLeft(event.currentTarget));
        setIsClosing(false);
        setIsMounted(true);
    };

    useEffect(() => {
        if (!isClosing) {
            return;
        }

        const timeout = setTimeout(unmount, MOTION.FAST_1_MS + 50);

        return () => clearTimeout(timeout);
    }, [isClosing, unmount]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const onResize = () => {
            if (!toggleButton.current) {
                return;
            }

            setTop(getHeaderBottom());
            setLeft(getCardLeft(toggleButton.current));
        };

        window.addEventListener('resize', onResize);

        return () => window.removeEventListener('resize', onResize);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close();
            }
        };

        const onPointerDown = (event: PointerEvent) => {
            const target = event.target;

            if (!(target instanceof Element)) {
                return;
            }

            const isInsideBox = target.closest(`[data-testid="${CATEGORIES_NAVIGATION_BOX_TEST_ID}"]`);
            const isToggleButton = target.closest(`[aria-controls="${CATEGORIES_NAVIGATION_OVERLAY_ID}"]`);

            if (!isInsideBox && !isToggleButton) {
                close();
            }
        };

        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('pointerdown', onPointerDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('pointerdown', onPointerDown);
        };
    }, [isOpen, close]);

    if (!isNewCategoriesNavigation) {
        return null;
    }

    return (
        <Container hidden={{ default: true, xl: false }}>
            <Button
                variant="ghost"
                onClick={toggle}
                aria-expanded={isOpen}
                aria-controls={CATEGORIES_NAVIGATION_OVERLAY_ID}
            >
                <Stack gap="2" alignItems="center">
                    <BarsIcon size="sm" color="grey_1200" />
                    <Text size="b_md" fontWeight="semibold" color="bodyPrimary" whiteSpace="nowrap">
                        <I18n k="header.categories_navigation.title" />
                    </Text>
                </Stack>
            </Button>
            {isMounted && (
                <Overlay
                    id={CATEGORIES_NAVIGATION_OVERLAY_ID}
                    dataTestId={CATEGORIES_NAVIGATION_OVERLAY_ID}
                    position="fixed"
                    background="grey"
                    opacity="low"
                    lockBodyScroll
                    onClick={close}
                    top={top}
                >
                    <Container
                        position="relative"
                        top="20px"
                        data-testid={CATEGORIES_NAVIGATION_BOX_TEST_ID}
                        className={isClosing ? panelExit : panelEnter}
                        onAnimationEnd={isClosing ? onExitAnimationEnd : undefined}
                        borderRadius="3xl"
                        boxShadow="aa"
                        backgroundColor="white"
                        width="fit-content"
                        style={{ marginInlineStart: left }}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <CategoriesMenuPanel categories={categories} />
                    </Container>
                </Overlay>
            )}
        </Container>
    );
};
